import { Icons } from '@/components/icons';
import type { JsonFilter } from '@/hooks/use-table-filter/types';
import { getConfig } from '@/lib/get-config';
import type { ModelName } from '@pigello/pigello-matrix';
import { Button } from '../ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../ui/command';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  CONNECTED_OBJECT_FILTER,
  type AppliedFilter,
  type ConnectedObjectFilterType,
  defaultRelationFilter,
  type Filter,
  OPERATORS,
} from './constants';

function getModelName(type: ConnectedObjectFilterType) {
  return type.contentType.split('.').at(1) as ModelName;
}

/** The relation filter used to pick and display instances of the chosen type. */
export function toConnectedObjectRelationFilter(
  filter: Filter,
  type: ConnectedObjectFilterType
): Filter {
  return {
    ...defaultRelationFilter,
    // __isnull makes no sense together with the content type filter
    operators: [...OPERATORS.IN],
    title: filter.title,
    externalFieldName: type.externalFieldName,
    relationModelName: getModelName(type),
    ...(filter.connectedObjectContentTypeFieldName && {
      connectedObjectContentType: {
        externalFieldName: filter.connectedObjectContentTypeFieldName,
        value: type.contentType,
      },
    }),
  };
}

/** Resolves an applied filter that was created through a CONNECTED_OBJECT_FILTER. */
export function getConnectedObjectFilterConfig(
  filters: Filter[],
  appliedFilter: AppliedFilter,
  currentFilter: JsonFilter | null
) {
  for (const filter of filters) {
    if (filter.kind !== CONNECTED_OBJECT_FILTER) continue;

    const contentTypeFieldName = filter.connectedObjectContentTypeFieldName;
    const type = filter.connectedObjectTypes?.find(
      (t) =>
        t.externalFieldName === appliedFilter.externalFieldName &&
        (!contentTypeFieldName ||
          currentFilter?.[contentTypeFieldName]?.noop === t.contentType)
    );
    if (!type) continue;

    const relationFilter = toConnectedObjectRelationFilter(filter, type);
    if (relationFilter.operators.includes(appliedFilter.operator)) {
      return relationFilter;
    }
  }
}

export function ConnectedObjectTypeModal({
  filter,
  onSelect,
  onClose,
}: {
  filter: Filter;
  onSelect: (type: ConnectedObjectFilterType) => void;
  onClose: () => void;
}) {
  return (
    <Dialog defaultOpen onOpenChange={(open) => !open && onClose()}>
      <DialogContent className='gap-0 p-0'>
        <DialogHeader>
          <DialogTitle>Välj typ av objekt</DialogTitle>
          <Button onClick={onClose} variant='outline' size='icon-sm'>
            <Icons.close className='size-4' />
          </Button>
          <DialogDescription className='sr-only'>
            Filtrera på {filter.title}
          </DialogDescription>
        </DialogHeader>
        <Command>
          <CommandInput placeholder='Sök bland typer...' />
          <CommandList className='max-h-100'>
            <CommandEmpty>Hittade inga typer.</CommandEmpty>
            <CommandGroup>
              {filter.connectedObjectTypes?.map((type) => {
                const verboseName = getConfig(getModelName(type))?.verboseName;
                return (
                  <CommandItem
                    key={type.contentType}
                    value={verboseName ?? type.contentType}
                    onSelect={() => onSelect(type)}
                  >
                    {verboseName ?? type.contentType}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
